import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Button,
  Badge,
  Tooltip,
  Progress,
  Tabs,
  Divider,
  Typography,
  List,
} from 'antd';

import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import Table from '@components/Table';
import DeviceActionsMenu from '@components/DeviceActionsMenu';
import DeviceActionModal from '@components/DeviceActionModal';

import {
  bytesFormat,
  dateFormat,
  extractDateInISOFormat,
  formatCurrency,
  formatPricePerUnit,
  getDateWithISOFormat,
} from '@utils/formatters';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';

import {
  ControlOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import useSwr from '@hooks/useSwr';
import { FiExternalLink } from 'react-icons/fi';
import SessionContext from '@providers/Session/SessionContext';
import CustomPopover from '@components/CustomPopover';
import RequestReport from '@components/RequestReport';
import RoleChecker from '@components/RoleChecker';
import useSearchParams from '@hooks/useSearchParams';
import { IObject } from '@utils/defaultTypes';
import { useNavigate } from 'react-router-dom';
import { QueryBuilder } from '@components/QueryBuilder';
import ActionTypes from '@utils/deviceActions';
import { FilterQueryItems } from '@components/QueryBuilder/types';
import { useInventoryFilters } from '@hooks/useInventoryFilters';
import BlockStatusTag from '@components/BlockStatusTag';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import Actions from '@components/Actions';
import ActionsModalByTypes from '@components/Actions/ActionsModal/ActionsModalByTypes';
import moment from 'moment';
import CustomTag from '@components/CustomTag';
import { getBlockStatusColor } from '@utils/getBlockStatusColor';
import RequestCancellationReport from '@components/RequestCancellationReport';
import { IDevice } from './types';
import LinesInfo from './LinesInfo';
import InventoryPreview from './InventoryPreview';

import * as S from './styles';

const { TabPane } = Tabs;

type SearchUploaderItems = 'msisdn' | 'imsi' | 'iccid';
const SEARCH_UPLOADER_ITEMS: SearchUploaderItems[] = [
  'msisdn',
  'imsi',
  'iccid',
];

interface IInventory {
  tableOnly?: boolean;
  defaultFilters?: IObject;
  defaultColumns?: string[];
}

const Inventory = ({
  tableOnly,
  defaultFilters,
  defaultColumns,
}: IInventory) => {
  const { t } = useTranslation(['inventory', 'device-actions-modal', 'status']);
  const { keycloak } = useContext(KeycloakContext);
  const {
    isRoot,
    hasPermission,
    isNotChildOfRoot,
    selectedApp,
    hasCustomersRoles,
    userPreferences: { getTablePreferences },
    isMot,
    isMotBusiness,
    isMotPay,
    isAllcom,
    isMotEnterprise,
    hasMotLorawan,
    customer: { isCustomerFinanciallyBlocked },
    theme,
  } = useContext(SessionContext);
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState<IDevice[]>();
  const [actionToExecute, setActionToExecute] = useState(-1);
  const [actionRole, setActionRole] = useState<RolesTypes>();
  const [modalDeviceActionsVisible, setModalDeviceActionsVisible] =
    useState<boolean>(false);
  const [modalDeviceActionsVisibleMot, setModalDeviceActionsVisibleMot] =
    useState<boolean>(false);
  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
  const [previewDevice, setPreviewDevice] = useState<IDevice | undefined>(
    undefined,
  );
  const [searchUploaderValues, setSearchUploaderValues] = useState<{
    [key in SearchUploaderItems]?: string[];
  }>();

  const customerTypeLogged = keycloak.idTokenParsed?.customerType;
  const isEndCustomer = customerTypeLogged === 'CUSTOMER';
  const isBroker = customerTypeLogged === 'BROKER';
  const isRootAndMot = isRoot && isMot;
  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const showSelectCustomer =
    selectedApp === ApplicationsConstants.MULTI_CONNECT ? isRoot : true;

  const {
    setSearchParams,
    clearSearchParams,
    searchParams,
    hasValuesInExtraParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['stock', 'showContracted'],
    extraParams: [
      'msisdn',
      'iccid',
      'imsi',
      'imei',
      'contractAssetNumber',
      'operator',
      'connectedOperator',
      'nickname',
      'observation',
      'device',
      'status',
      'franchise',
      'customerName',
      'customerDocumentNumber',
      'customerId',
      'online',
      'city',
      'state',
      'postalCode',
      'lastConnectionFrom',
      'lastConnectionTo',
      'consumptionPercentFrom',
      'consumptionPercentTo',
      'consumptionFrom',
      'consumptionTo',
      'automaticBlocking',
      'networkBlockStatus',
      'blockedByDefault',
      'accountId',
      'accountDesc',
      'loyaltyType',
      'loyaltyTime',
      'cancellationFeeFrom',
      'cancellationFeeTo',
      'activationPriceFrom',
      'activationPriceTo',
      'priceFrom',
      'priceTo',
      'megabyteExcPriceFrom',
      'megabyteExcPriceTo',
      'simcardCreditFrom',
      'simcardCreditTo',
      'tradeInMonths',
      'preActivationDays',
      'preActivationEndDateFrom',
      'preActivationEndDateTo',
      'sharingType',
      'brokerContractId',
      'brokerContractDesc',
      'sharedContractCreditFrom',
      'sharedContractCreditTo',
      'sharedContractFranchiseFrom',
      'sharedContractFranchiseTo',
      'apn',
      'provisionedApn',
      'hostname',
      'technology',
      'imeiLocked',
      'activationDateFrom',
      'activationDateTo',
      'cancellationDateFrom',
      'cancellationDateTo',
      'suspensionEligibility',
      'suspensionStartDateFrom',
      'suspensionStartDateTo',
      'suspensionEndDateFrom',
      'suspensionEndDateTo',
      'plan',
      'cancellationDate',
      'lastStatusUpdate',
      'networkBlockEndDate',
      'networkBlockStartDate',
      'planTypes',
      'deviceNumber',
      'lastAlertDateFrom',
    ],
  });

  const {
    deviceNumber,
    lastAlertDateFrom,
    imei,
    planTypes,
    contractAssetNumber,
    showContracted,
    operator,
    connectedOperator,
    nickname,
    observation,
    device,
    status,
    stock,
    franchise,
    customerName,
    customerDocumentNumber,
    customerId,
    online,
    city,
    state,
    postalCode,
    lastConnectionFrom,
    lastConnectionTo,
    consumptionPercentFrom,
    consumptionPercentTo,
    consumptionFrom,
    consumptionTo,
    automaticBlocking,
    networkBlockStatus,
    blockedByDefault,
    accountId,
    accountDesc,
    loyaltyType,
    loyaltyTime,
    cancellationFeeFrom,
    cancellationFeeTo,
    activationPriceFrom,
    activationPriceTo,
    priceFrom,
    priceTo,
    megabyteExcPriceFrom,
    megabyteExcPriceTo,
    simcardCreditFrom,
    simcardCreditTo,
    tradeInMonths,
    preActivationDays,
    preActivationEndDateFrom,
    preActivationEndDateTo,
    sharingType,
    brokerContractId,
    brokerContractDesc,
    sharedContractCreditFrom,
    sharedContractCreditTo,
    sharedContractFranchiseFrom,
    sharedContractFranchiseTo,
    apn,
    provisionedApn,
    hostname,
    technology,
    imeiLocked,
    activationDateFrom,
    activationDateTo,
    cancellationDateFrom,
    cancellationDateTo,
    suspensionEligibility,
    suspensionStartDateFrom,
    suspensionStartDateTo,
    suspensionEndDateFrom,
    suspensionEndDateTo,
    lastStatusUpdate,
    cancellationDate,
    networkBlockEndDate,
    networkBlockStartDate,
    plan,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const handleSearchUploaderFilter = useCallback(
    (key: SearchUploaderItems) =>
      searchUploaderValues?.[key] ?? searchParams?.[key]?.split(','),
    [searchParams, searchUploaderValues],
  );

  const filters = useMemo(() => {
    const allFilters = {
      stock: undefined,
      showContracted: showContracted || 'false',
      msisdn: handleSearchUploaderFilter('msisdn'),
      imsi: handleSearchUploaderFilter('imsi'),
      iccid: handleSearchUploaderFilter('iccid'),
      imei,
      deviceNumber,
      online,
      connectedOperator,
      status,
      nickname,
      apn,
      provisionedApn,
      plan,
      planTypes: planTypes && planTypes.split(','),
      technology,
      lastConnectionFrom:
        lastConnectionFrom && getDateWithISOFormat(lastConnectionFrom, 'start'),
      lastConnectionTo:
        lastConnectionTo && getDateWithISOFormat(lastConnectionTo, 'end'),
      activationDateFrom:
        activationDateFrom && getDateWithISOFormat(activationDateFrom, 'start'),
      activationDateTo:
        activationDateTo && getDateWithISOFormat(activationDateTo, 'end'),
      consumptionFrom: consumptionFrom && Number(consumptionFrom) * 100000,
      consumptionTo: consumptionTo && Number(consumptionTo) * 100000,
      customerId,
      contractAssetNumber,
      operator,
      observation,
      device,
      franchise: franchise && Number(franchise),
      customerName,
      customerDocumentNumber,
      city,
      lastAlertDateFrom,
      state,
      postalCode,
      accountId,
      accountDesc,
      loyaltyType,
      loyaltyTime,
      tradeInMonths,
      preActivationDays,
      sharingType,
      brokerContractId,
      brokerContractDesc,
      hostname,
      consumptionPercentFrom:
        consumptionPercentFrom && Number(consumptionPercentFrom),
      consumptionPercentTo:
        consumptionPercentTo && Number(consumptionPercentTo),
      automaticBlocking,
      networkBlockStatus,
      blockedByDefault,
      cancellationFeeFrom: cancellationFeeFrom && Number(cancellationFeeFrom),
      cancellationFeeTo: cancellationFeeTo && Number(cancellationFeeTo),
      activationPriceFrom: activationPriceFrom && Number(activationPriceFrom),
      activationPriceTo: activationPriceTo && Number(activationPriceTo),
      priceFrom: priceFrom && Number(priceFrom),
      priceTo: priceTo && Number(priceTo),
      megabyteExcPriceFrom:
        megabyteExcPriceFrom && Number(megabyteExcPriceFrom),
      megabyteExcPriceTo: megabyteExcPriceTo && Number(megabyteExcPriceTo),
      simcardCreditFrom: simcardCreditFrom && Number(simcardCreditFrom),
      simcardCreditTo: simcardCreditTo && Number(simcardCreditTo),
      preActivationEndDateFrom:
        preActivationEndDateFrom &&
        getDateWithISOFormat(preActivationEndDateFrom, 'start'),
      preActivationEndDateTo:
        preActivationEndDateTo &&
        getDateWithISOFormat(preActivationEndDateTo, 'end'),
      cancellationDateFrom:
        cancellationDateFrom &&
        getDateWithISOFormat(cancellationDateFrom, 'start'),
      cancellationDateTo:
        cancellationDateTo && getDateWithISOFormat(cancellationDateTo, 'end'),
      suspensionStartDateFrom:
        suspensionStartDateFrom &&
        getDateWithISOFormat(suspensionStartDateFrom, 'start'),
      suspensionStartDateTo:
        suspensionStartDateTo &&
        getDateWithISOFormat(suspensionStartDateTo, 'end'),
      suspensionEndDateFrom:
        suspensionEndDateFrom &&
        getDateWithISOFormat(suspensionEndDateFrom, 'start'),
      suspensionEndDateTo:
        suspensionEndDateTo && getDateWithISOFormat(suspensionEndDateTo, 'end'),
      sharedContractCreditFrom:
        sharedContractCreditFrom && Number(sharedContractCreditFrom),
      sharedContractCreditTo:
        sharedContractCreditTo && Number(sharedContractCreditTo),
      sharedContractFranchiseFrom:
        sharedContractFranchiseFrom && Number(sharedContractFranchiseFrom),
      sharedContractFranchiseTo:
        sharedContractFranchiseTo && Number(sharedContractFranchiseTo),
      lastStatusUpdate,
      cancellationDate,
      networkBlockEndDate,
      networkBlockStartDate,
      imeiLocked,
      suspensionEligibility,
      ...defaultFilters,
    };

    if (!isRoot && !tableOnly && isEndCustomer) {
      allFilters.showContracted = 'true';
    }
    if (isMotPay && !isEndCustomer && !isRoot) {
      allFilters.showContracted = 'true';
    }
    if (!isMot) {
      allFilters.showContracted = true;
    }
    if (!isRoot && tableOnly && isEndCustomer && isMot) {
      allFilters.showContracted = 'true';
    }
    allFilters.stock = stock !== 'all' ? stock : undefined;

    return allFilters;
  }, [
    showContracted,
    handleSearchUploaderFilter,
    imei,
    deviceNumber,
    online,
    connectedOperator,
    status,
    nickname,
    apn,
    provisionedApn,
    plan,
    planTypes,
    technology,
    lastConnectionFrom,
    lastConnectionTo,
    activationDateFrom,
    activationDateTo,
    consumptionFrom,
    consumptionTo,
    customerId,
    contractAssetNumber,
    operator,
    observation,
    device,
    franchise,
    customerName,
    customerDocumentNumber,
    city,
    lastAlertDateFrom,
    state,
    postalCode,
    accountId,
    accountDesc,
    loyaltyType,
    loyaltyTime,
    tradeInMonths,
    preActivationDays,
    sharingType,
    brokerContractId,
    brokerContractDesc,
    hostname,
    consumptionPercentFrom,
    consumptionPercentTo,
    automaticBlocking,
    networkBlockStatus,
    blockedByDefault,
    cancellationFeeFrom,
    cancellationFeeTo,
    activationPriceFrom,
    activationPriceTo,
    priceFrom,
    priceTo,
    megabyteExcPriceFrom,
    megabyteExcPriceTo,
    simcardCreditFrom,
    simcardCreditTo,
    preActivationEndDateFrom,
    preActivationEndDateTo,
    cancellationDateFrom,
    cancellationDateTo,
    suspensionStartDateFrom,
    suspensionStartDateTo,
    suspensionEndDateFrom,
    suspensionEndDateTo,
    sharedContractCreditFrom,
    sharedContractCreditTo,
    sharedContractFranchiseFrom,
    sharedContractFranchiseTo,
    lastStatusUpdate,
    cancellationDate,
    networkBlockEndDate,
    networkBlockStartDate,
    imeiLocked,
    suspensionEligibility,
    defaultFilters,
    isRoot,
    tableOnly,
    isEndCustomer,
    isMotPay,
    isMot,
    stock,
  ]);

  const { data: inventoryData, mutate } = useSwr(
    `/service-proxy/inventory/${routeToFindData}`,
    {
      method: 'post',
      data: {
        filters,
        pagination: {
          page: page ? Number(page) - 1 : 0,
          linesPerPage: linesPerPage ?? 20,
        },
      },
    },
  );

  const { data: eventsAlertsData } = useSwr(
    lastAlertDateFrom ? `/service-proxy/alert-events/summary` : null,
    {
      params: {
        dateFrom: new Date(lastAlertDateFrom),
      },
    },
    {
      refreshInterval: 300000,
    },
  );

  const handleMutateInventoryData = (newData: any) => {
    mutate(newData);
  };

  const togglePreview = useCallback((record: IDevice) => {
    setPreviewDevice(record);
    setIsOpenPreview(true);
  }, []);

  const handleActionClick = useCallback((key: number) => {
    if (key === ActionTypes.CHANGE_SIMCARD) {
      setActionRole(rolesConstants.CHANGE_SIMCARD);
      setModalDeviceActionsVisibleMot(true);
      return;
    }
    setActionToExecute(key);
    setModalDeviceActionsVisible(true);
  }, []);

  const columnConsumption = useCallback(
    (value: any, record: any) => {
      const {
        consumptionTotal,
        consumptionCurrentDay,
        consumptionPercent,
        consumptionValidated,
        simcardCredit,
      } = record;

      const percent = consumptionPercent || 0;

      const percentValidated =
        consumptionValidated && consumptionTotal
          ? (Number(consumptionValidated) * consumptionPercent) /
            (consumptionTotal + (simcardCredit ?? 0))
          : 0;

      let strokeColorDark = '#27B502';
      let strokeColor = '#2FD105';
      if (percent >= 80 && percent <= 100) {
        strokeColorDark = '#EA9801';
        strokeColor = '#ffa500';
      } else if (percent > 100) {
        strokeColorDark = '#CD022A';
        strokeColor = '#EC0231';
      }

      const sizeConsumption = bytesFormat(value);
      const sizeConsumptionCurrentDay = bytesFormat(consumptionCurrentDay);
      const sizeConsumptionValidated = bytesFormat(consumptionValidated);
      const sizeFranchising = bytesFormat(
        record.franchise + (simcardCredit || 0),
      );
      const dataContentPopover = [
        {
          title: t('consumption.validated'),
          value: sizeConsumptionValidated,
          color: strokeColor,
          show: consumptionValidated,
        },
        {
          title: t('consumption.daily'),
          value: sizeConsumptionCurrentDay,
          color: strokeColorDark,
          show: consumptionCurrentDay,
        },
      ];

      return (
        <div className="col-line-consumption">
          {percent !== 0 && (
            <div
              style={{
                margin: -4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip
                title={`${
                  percent && percent >= 1
                    ? Math.trunc(percent)
                    : percent?.toFixed(2)
                }%`}
              >
                <Progress
                  percent={percent}
                  showInfo={false}
                  strokeColor={strokeColorDark}
                  success={{
                    percent: percentValidated,
                    strokeColor,
                  }}
                  className="progress-bar-consumption"
                />
              </Tooltip>

              <CustomPopover
                title={t('table.consumption')}
                titleValue={sizeConsumption}
                titleContent=""
                dataContent={dataContentPopover.filter(
                  item => item.show !== 0 && item.show,
                )}
              >
                <Button
                  type="link"
                  style={{ padding: 0, margin: 0, marginLeft: 6 }}
                >
                  <PlusCircleOutlined />
                </Button>
              </CustomPopover>
            </div>
          )}

          {((value && value !== 0) ||
            (simcardCredit && simcardCredit !== 0) ||
            (record.franchise && record.franchise !== 0)) && (
            <p style={{ margin: 0, marginTop: -4, fontSize: '0.75rem' }}>
              {percent
                ? `${sizeConsumption} / ${sizeFranchising}`
                : sizeConsumption}
            </p>
          )}
        </div>
      );
    },
    [t],
  );

  const columnApn = useCallback(
    (value: string[]) => {
      return (
        value?.length > 0 && (
          <Tooltip
            title={
              <>
                <List
                  dataSource={value}
                  renderItem={item => (
                    <List.Item style={{ padding: '18px' }}>
                      <Typography.Text>{item}</Typography.Text>
                    </List.Item>
                  )}
                  style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    backgroundColor: 'white',
                  }}
                />
              </>
            }
            className="apn-tooltip"
          >
            <Button type="link">
              {`${value.length} ${t('attributes:apn')}`}
            </Button>
          </Tooltip>
        )
      );
    },
    [t],
  );

  const contractDescriptionLink = useCallback(
    (value: string, record: any) => {
      const initialPath = `/${selectedApp}/${
        hasMotLorawan ? 'mobile-network/' : ''
      }`;
      const middlePath = isMotBusiness ? 'administrative' : 'financial';
      const endPath = `/contracts/${record.brokerContractId}${
        showContracted ? `?showContracted=${showContracted}` : ''
      }`;
      return record.brokerContractId ? (
        <Tooltip title={t('view-contract')}>
          <Button
            type="link"
            onClick={() => {
              navigate(`${initialPath}${middlePath}${endPath}`);
            }}
          >
            {value}
          </Button>
        </Tooltip>
      ) : (
        value
      );
    },
    [selectedApp, hasMotLorawan, isMotBusiness, showContracted, t, navigate],
  );

  const accountDescriptionLink = useCallback(
    (value: string, record: any) => {
      const initialPath = `/${selectedApp}/${
        hasMotLorawan ? 'mobile-network/' : ''
      }`;
      const middlePath = isMotBusiness
        ? 'administrative/groupings'
        : 'financial/requests';
      const endPath = `/${record.accountId}${
        showContracted ? `?showContracted=${showContracted}` : ''
      }`;

      return record.accountId ? (
        <Tooltip title={t(`view-${isMotBusiness ? 'grouping' : 'request'}`)}>
          <Button
            type="link"
            onClick={() => {
              navigate(`${initialPath}${middlePath}${endPath}`);
            }}
          >
            {value}
          </Button>
        </Tooltip>
      ) : (
        value
      );
    },
    [selectedApp, hasMotLorawan, isMotBusiness, showContracted, t, navigate],
  );

  const columnFranchise = useCallback(
    (value: any, record: any) => {
      const { simcardCredit, sharedCredit } = record;

      const dataContentPopover = [
        {
          title: t('franchise.sim-card'),
          value: `+ ${bytesFormat(simcardCredit)}`,
          color: 'green',
          show: simcardCredit,
        },
        {
          title: t('franchise.contract'),
          value: `+ ${bytesFormat(sharedCredit)}`,
          color: 'blue',
          show: sharedCredit,
        },
      ];

      const sizeFranchising = bytesFormat(value);
      const hasCredit =
        (sharedCredit && sharedCredit !== 0) ||
        (simcardCredit && simcardCredit !== 0);

      return (
        <div>
          {value && value !== 0 ? (
            <>
              <span style={{ margin: 0, fontSize: '0.86rem' }}>
                {sizeFranchising}
              </span>
              {hasCredit ? (
                <CustomPopover
                  title={t('table.franchising')}
                  titleValue={bytesFormat(value)}
                  titleContent={t('franchise.additional')}
                  dataContent={dataContentPopover.filter(
                    item => item.show !== 0 && item.show,
                  )}
                >
                  <Button type="link" style={{ padding: 0, marginLeft: 6 }}>
                    <PlusCircleOutlined />
                  </Button>
                </CustomPopover>
              ) : (
                <></>
              )}
            </>
          ) : undefined}
        </div>
      );
    },
    [t],
  );

  const renderBadgeStatus = useCallback(
    (value: any, isSuspension?: boolean) => {
      const statusColor = value ? theme?.colors?.green : theme?.colors?.red;
      let text = value ? 'Online' : 'Offline';

      if (isSuspension) {
        text = t(
          `options.suspensionEligibility.${
            value ? 'eligible' : 'not-eligible'
          }`,
        );
      }

      return (
        <div
          style={{
            color: statusColor,
            display: 'inline-flex',
            alignItems: 'center',
            fontWeight: 'bold',
          }}
        >
          <Badge color={statusColor} style={{ marginRight: 8 }} />
          {text}
        </div>
      );
    },
    [theme, t],
  );

  const columns = useMemo(() => {
    const motColumns = [
      ...(((showContracted === 'false' || !showContracted) &&
        !isNotChildOfRoot &&
        !isRoot) ||
      (!isRoot && isNotChildOfRoot && showContracted === 'true')
        ? [
            {
              title: t('table.roaming'),
              dataIndex: 'roaming',
              align: 'center',
              render: (value: number) => (
                <CustomTag color={value ? 'green' : 'red'}>
                  {value
                    ? t(`options.autoBlock.block`)
                    : t(`options.autoBlock.unblock`)}
                </CustomTag>
              ),
            },
            {
              title: t('table.mbExcPrice'),
              dataIndex: 'mbExcPrice',
              align: 'center',
              render: formatPricePerUnit,
            },
            {
              title: t('table.penalty'),
              dataIndex: 'penalty',
              render: (value: any) => formatCurrency(Number(value || 0)),
            },
            {
              title: t('table.eligibleToSuspension'),
              dataIndex: 'eligibleToSuspension',
              align: 'center',
              render: (value: any) => renderBadgeStatus(value, true),
            },
            {
              title: t('table.franchising'),
              dataIndex: 'franchise',
              align: 'center' as const,
              render: (value: any, record: any) =>
                columnFranchise(value, record),
            },
          ]
        : []),
      {
        title: t('table.automaticBlocking'),
        dataIndex: 'automaticBlocking',
        align: 'center',
        render: (value: number) => (
          <CustomTag color={value ? 'red' : 'green'}>
            {value
              ? t(`options.autoBlock.block`)
              : t(`options.autoBlock.unblock`)}
          </CustomTag>
        ),
      },
      ...(isNotChildOfRoot && isEndCustomer
        ? [
            {
              title: t('table.mbExcPrice'),
              dataIndex: 'mbExcPrice',
              align: 'center',
              render: formatPricePerUnit,
            },
          ]
        : []),
      ...(!isRoot && !(!isNotChildOfRoot && showContracted === 'true')
        ? [
            {
              title: t('table.brokerContractDesc'),
              dataIndex: 'brokerContractDesc',
              render: contractDescriptionLink,
            },
            {
              title: t(`table.${isMotBusiness ? 'groupings' : 'accountDesc'}`),
              dataIndex: 'accountDesc',
              render: accountDescriptionLink,
            },
            {
              title: t('table.blockedByDefault'),
              dataIndex: 'blockedByDefault',
              align: 'center',
              render: (value: number) => (
                <CustomTag color={value ? 'red' : 'green'}>
                  {value
                    ? t(`options.blocked.block`)
                    : t(`options.blocked.unblock`)}
                </CustomTag>
              ),
            },
            {
              title: t('table.sharingType'),
              dataIndex: 'sharingType',
              render: (value: string) =>
                t(
                  `options.sharingType.${value ? value.toLowerCase() : 'none'}`,
                ),
            },
            {
              title: t('table.preActivationEnd'),
              dataIndex: 'preActivationEnd',
              align: 'center',
              render: extractDateInISOFormat,
            },
            {
              title: t('table.tradeInMonths'),
              dataIndex: 'tradeInMonths',
              align: 'center',
            },
            {
              title: t('table.price'),
              dataIndex: 'price',
              render: (value: any) => formatCurrency(Number(value || 0)),
            },
            {
              title: t('table.activation-price'),
              dataIndex: 'activationPrice',
              align: 'center',
              render: (value: any) => formatCurrency(Number(value || 0)),
            },
            {
              title: t('table.loyaltyType'),
              dataIndex: 'loyaltyType',
              align: 'center',
              render: (value: string) =>
                t(`options.typesOfPenalty.${value ? String(value) : '0'}`),
            },
            {
              title: t('table.preActivationDays'),
              dataIndex: 'preActivationDays',
              align: 'center',
            },
          ]
        : []),
      {
        title: t('table.parentName'),
        dataIndex: 'parentName',
        align: 'center',
      },
      ...(!isEndCustomer
        ? [
            {
              title: t(`table.${isMotBusiness ? 'department' : 'client'}`),
              dataIndex: 'customerName',
            },
            {
              title: t(
                `table.${
                  isMotBusiness ? 'documentNumber' : 'customerDocumentNumber'
                }`,
              ),
              dataIndex: 'customerDocumentNumber',
            },
          ]
        : []),
      ...(isRoot || (!isNotChildOfRoot && showContracted === 'true')
        ? [
            {
              title: t(`table.${isMotBusiness ? 'request' : 'plan'}`),
              dataIndex: 'plan',
            },
            {
              title: t('table.contractAssetNumber'),
              dataIndex: 'contractAssetNumber',
            },
          ]
        : []),
      {
        title: t('table.operator'),
        dataIndex: 'operator',
        align: 'center' as const,
      },
      {
        title: t('table.lastAlertDate'),
        dataIndex: 'lastAlertDate',
        render: (value: string) => value && dateFormat(value),
      },
      ...(isMotEnterprise &&
      ((showContracted === 'false' && isBroker) ||
        (isEndCustomer && showContracted === 'true'))
        ? [
            {
              title: t('table.planType'),
              dataIndex: 'planType',
              render: (value: string) =>
                value && t(`attributes:planTypes.${value}`),
            },
          ]
        : []),
      ...(isMotEnterprise &&
      ((isBroker && showContracted !== 'true') || isEndCustomer)
        ? [
            {
              title: t('table.iscaMaxRenewal'),
              dataIndex: 'iscaMaxRenewal',
              align: 'center' as const,
            },
            {
              title: t('table.nuRenewals'),
              dataIndex: 'nuRenewals',
              align: 'center' as const,
            },
          ]
        : []),
      // ...(isMotEnterprise
      //   ? [
      //       {
      //         title: t('table.automaticRenewal'),
      //         dataIndex: 'automaticRenewal',
      //       },
      //       {
      //         title: t('table.iscaMaxRenewal'),
      //         dataIndex: 'iscaMaxRenewal',
      //       },
      //     ]
      //   : []),
    ].filter(
      col =>
        // @ts-ignore
        col && hasPermission(col.dataIndex, 'inventory'),
    );

    const multiConnectColumns = [
      ...(selectedApp === ApplicationsConstants.CONNECT_ONE
        ? [
            {
              title: t('table.origin-operator'),
              dataIndex: 'operator',
              align: 'center' as const,
            },
          ]
        : []),
      {
        title: t('table.zipcode'),
        dataIndex: 'zipCode',
      },
      {
        title: t('table.franchising'),
        dataIndex: 'franchise',
        align: 'center' as const,
        render: (value: any, render: any) =>
          value ? columnFranchise(value, render) : undefined,
      },
      {
        title: t('table.planType'),
        dataIndex: 'planType',
        render: (value: string) => value && t(`attributes:planTypes.${value}`),
      },
      {
        title: t(`table.client`),
        dataIndex: 'customerName',
      },
      {
        title: t('table.plan'),
        dataIndex: 'plan',
      },
      { title: t('table.device-info'), dataIndex: 'deviceInfo' },
      {
        title: t('table.blocking-status'),
        dataIndex: 'blockingType',
        align: 'center' as const,
        render: (value: string, record: any) => (
          <BlockStatusTag
            blockType={value}
            blockingInitialDate={record.blockingInitialDate}
            blockingFinalDate={record.blockingFinalDate}
          />
        ),
      },
      {
        title: t(`table.customerDocumentNumber`),
        dataIndex: 'customerDocumentNumber',
      },
      {
        title: t('table.contractAssetNumber'),
        dataIndex: 'contractAssetNumber',
      },
    ].filter(
      col =>
        // @ts-ignore
        col && hasPermission(col.dataIndex, 'inventory'),
    );

    const allColumns = [
      ...[
        {
          title: t('table.lastApnConnected'),
          dataIndex: 'apn',
        },
        {
          title: t('table.provisionedApn'),
          dataIndex: 'apns',
          align: 'center',
          render: (value: string[]) => (value ? columnApn(value) : undefined),
        },
        {
          title: t('table.consumption'),
          dataIndex: 'consumptionTotal',
          align: 'center' as const,
          render: (value: any, render: any) =>
            value ? columnConsumption(value, render) : undefined,
        },
        {
          title: t('table.connected-operator'),
          dataIndex: 'connectedOperator',
          align: 'center' as const,
        },
        {
          title: t('table.technology'),
          dataIndex: 'technology',
          align: 'center' as const,
        },
        {
          title: t('table.network-status'),
          dataIndex: 'online',
          align: 'center' as const,
          render: (value: any) => renderBadgeStatus(value),
        },
        {
          title: t('table.city'),
          dataIndex: 'city',
        },
        {
          title: t('table.state'),
          dataIndex: 'state',
        },

        {
          title: t('table.nickname'),
          dataIndex: 'nickname',
        },
        {
          title: t('table.caller-id'),
          dataIndex: 'msisdn',
          width: `${t('table.caller-id').length + 6}ch`,
        },
        {
          title: t('table.iccid'),
          dataIndex: 'iccid',
        },
        {
          title: t('table.imsi'),
          dataIndex: 'imsi',
        },
        {
          title: t('table.imei'),
          dataIndex: 'imei',
        },
        { title: t('table.hostname'), dataIndex: 'hostname' },
        {
          title: t('table.last-connection'),
          dataIndex: 'lastConnection',
          render: dateFormat,
        },
        {
          title: t('table.status'),
          dataIndex: 'status',
          align: 'center' as const,
          render: (value: string) =>
            value && (
              <CustomTag color={getBlockStatusColor(value)}>
                {t(`status:${value}`)}
              </CustomTag>
            ),
        },
        {
          title: t('table.activation-date'),
          dataIndex: 'activationDate',
          align: 'center' as const,
          render: extractDateInISOFormat,
        },
        {
          title: t('table.lastStatusUpdate'),
          dataIndex: 'lastStatusUpdate',
          align: 'center' as const,
          render: extractDateInISOFormat,
        },
        {
          title: t('table.lastUpdate'),
          dataIndex: 'lastUpdate',
          align: 'center' as const,
          render: extractDateInISOFormat,
        },
        ...(!isMotBusiness
          ? [
              {
                title: t('table.suspensionStartDate'),
                dataIndex: 'suspensionStartDate',
                align: 'center' as const,
                render: extractDateInISOFormat,
              },
              {
                title: t('table.suspensionEndDate'),
                dataIndex: 'suspensionEndDate',
                align: 'center' as const,
                render: extractDateInISOFormat,
              },
            ]
          : []),
        { title: t('table.observation'), dataIndex: 'observation' },
        { title: t('table.latitude'), dataIndex: 'latitude' },
        { title: t('table.longitude'), dataIndex: 'longitude' },
        { title: t('table.postalCode'), dataIndex: 'postalCode' },
        {
          title: t('table.imeiLocked'),
          dataIndex: 'imeiLocked',
          align: 'center',
          render: (value: boolean) => {
            const [statusColor, statusType]: [
              'red' | 'green',
              'lock' | 'unlock',
            ] = value ? ['red', 'lock'] : ['green', 'unlock'];

            return (
              <CustomTag color={statusColor}>
                {t(`options.imeiLocked.${statusType}`).toUpperCase()}
              </CustomTag>
            );
          },
        },
        {
          title: t('table.blocking-status'),
          dataIndex: 'networkBlockStatus',
          align: 'center' as const,
          render: (value: string, record: any) => (
            <BlockStatusTag
              blockType={value}
              blockingInitialDate={record.networkBlockStartDate}
              blockingFinalDate={record.networkBlockEndDate}
            />
          ),
        },
        {
          title: t('table.cancellationDate'),
          dataIndex: 'cancellationDate',
          align: 'center' as const,
          render: extractDateInISOFormat,
        },
      ].filter(
        col =>
          // @ts-ignore
          col && hasPermission(col.dataIndex, 'inventory'),
      ),
    ];

    if (isMotPay) {
      // @ts-ignore
      allColumns.push(
        ...[
          {
            title: t('table.deviceNumber'),
            dataIndex: 'deviceNumber',
          },
          {
            title: 'GGSN',
            dataIndex: 'ggsn',
          },
        ],
      );
    }

    if (isMot) {
      // @ts-ignore
      allColumns.push(...motColumns);
    } else {
      // @ts-ignore
      allColumns.push(...multiConnectColumns);
    }

    allColumns.unshift({
      title: t('table.details'),
      dataIndex: 'actions',
      align: 'center' as const,
      render: (_: any, deviceOpen: any) => {
        const haveLastAlert =
          isMot &&
          Boolean(deviceOpen.lastAlertDate) &&
          moment(deviceOpen.lastAlertDate).isAfter(
            moment().subtract(7, 'days'),
          );

        return (
          <>
            <Button
              style={{ padding: '0 8px' }}
              onClick={() => togglePreview(deviceOpen)}
            >
              <FileSearchOutlined />
            </Button>
            {haveLastAlert && (
              <Tooltip title={t('attributes:newAlert')}>
                <Button danger type="link" icon={<WarningOutlined />} />
              </Tooltip>
            )}
          </>
        );
      },
    });

    if (defaultColumns) {
      return allColumns.filter(
        column => column && defaultColumns.includes(column.dataIndex),
      );
    }
    return allColumns;
  }, [
    showContracted,
    isNotChildOfRoot,
    isRoot,
    t,
    isEndCustomer,
    contractDescriptionLink,
    isMotBusiness,
    accountDescriptionLink,
    isMotEnterprise,
    isBroker,
    selectedApp,
    isMotPay,
    isMot,
    defaultColumns,
    renderBadgeStatus,
    columnFranchise,
    hasPermission,
    columnApn,
    columnConsumption,
    togglePreview,
  ]);

  const isUpload = useMemo(
    () => !(selectedRows && selectedRows.length > 0),
    [selectedRows],
  );

  const extraActions = useMemo(() => {
    const isMenuDisabled =
      !hasPermission(rolesConstants.BATCH_ACTIONS) &&
      selectedRows?.length !== 1;

    return (
      <>
        {!isNotChildOfRoot &&
          !tableOnly &&
          !(isAllcom && isMot) &&
          !isCustomerFinanciallyBlocked && (
            <RoleChecker role={rolesConstants.SIMCARD_IMPORT}>
              <Tooltip
                title={
                  isRoot && isMot
                    ? t('root-cannot-see')
                    : t('report.request-activate-simcards')
                }
              >
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={() => {
                    if (isMot) {
                      setActionRole(rolesConstants.SIMCARD_IMPORT);
                      setModalDeviceActionsVisibleMot(true);
                    } else {
                      handleActionClick(ActionTypes.ACTIVATION_MULTI_CONNECT);
                    }
                  }}
                  disabled={isRootAndMot}
                />
              </Tooltip>
            </RoleChecker>
          )}
        <RoleChecker role={rolesConstants.REQUEST_REPORT}>
          <RequestReport
            items={[
              {
                role: rolesConstants.REQUEST_REPORT,
                filters,
                path: `/service-proxy/inventory/${routeToFindData}/report`,
                type: `${routeToFindData}_INVENTORY`,
                method: 'post',
              },
            ]}
          />
          {!isNotChildOfRoot && <RequestCancellationReport />}
        </RoleChecker>
        {isMot ? (
          !isCustomerFinanciallyBlocked && (
            <Actions.Menu
              isStock={stock === 'true'}
              showContracted={showContracted}
              onActionClick={(role: RolesTypes) => {
                setActionRole(role);
                if (role === rolesConstants.SEND_SMS) {
                  handleActionClick(124);
                  return;
                }
                setModalDeviceActionsVisibleMot(true);
              }}
              isUpload={isUpload}
            />
          )
        ) : (
          <DeviceActionsMenu
            isStock={stock === 'true'}
            button={
              <Tooltip
                title={t(
                  `table.${isMenuDisabled ? 'batch-not-allowed' : 'actions'}`,
                )}
              >
                <Button
                  icon={
                    <ControlOutlined
                      style={{
                        fontSize: '1.185rem',
                      }}
                    />
                  }
                  disabled={isMenuDisabled}
                />
              </Tooltip>
            }
            showContracted={showContracted}
            onActionClick={({ key }) => handleActionClick(key)}
            disabled={isMenuDisabled}
            isUpload={isUpload}
          />
        )}
      </>
    );
  }, [
    hasPermission,
    selectedRows?.length,
    isNotChildOfRoot,
    tableOnly,
    isAllcom,
    isMot,
    isCustomerFinanciallyBlocked,
    isRoot,
    t,
    isRootAndMot,
    filters,
    routeToFindData,
    stock,
    showContracted,
    isUpload,
    handleActionClick,
  ]);

  const mainExtraActions = useMemo(
    () =>
      tableOnly && (
        <>
          <Divider type="vertical" style={{ marginLeft: 16 }} />
          <Tooltip title={t('requests:viewOnInventoryPage')}>
            <Button
              icon={
                <FiExternalLink
                  style={{
                    marginTop: 4,
                  }}
                />
              }
              onClick={() => {
                navigate(
                  `/${selectedApp}/${
                    hasMotLorawan ? 'mobile-network/' : ''
                  }assets/inventory?showContracted=${
                    defaultFilters?.showContracted
                  }&accountId=${defaultFilters?.accountId}`,
                );
              }}
            />
          </Tooltip>
        </>
      ),
    [
      tableOnly,
      t,
      navigate,
      selectedApp,
      hasMotLorawan,
      defaultFilters?.showContracted,
      defaultFilters?.accountId,
    ],
  );

  const tabsOptions = useMemo(
    () => [
      { label: t('tabs.stock'), key: 'true' },
      { label: t('tabs.allocated'), key: 'false' },
    ],
    [t],
  );

  const tableKey = useMemo(() => {
    let vision = '';
    if (!isRoot && !isEndCustomer && isMot) {
      vision = showContracted === 'true' ? 'Pay' : 'Allocated';
    }
    return `${selectedApp?.replaceAll('_', '')}Inventory${vision}`;
  }, [isEndCustomer, isMot, isRoot, selectedApp, showContracted]);

  const handleTabFilterChange = useCallback(
    (value: string) => {
      if (value === 'all') {
        clearSearchParams(['stock']);
      } else {
        setSearchParams({ stock: value });
      }
    },
    [clearSearchParams, setSearchParams],
  );

  const { filtersData } = useInventoryFilters({
    showContracted: showContracted === 'true',
    showSelectCustomer,
    isEndCustomer,
    isBroker,
  });

  const defaultValues = useCallback(
    (defaultShowContracted?: string) => {
      const tablePreferences = getTablePreferences(tableKey);
      const defaultValuesString = tablePreferences?.filterSelected || [
        'msisdn',
        'iccid',
        'imsi',
        'networkBlockStatus',
        'online',
        'status',
        'operator',
        'connectedOperator',
        'lastConnectionFrom',
        'automaticBlocking',
        'technology',
        'apn',
        'provisionedApn',
        'activationDateFrom',
        'franchise',
        'nickname',
        'imei',
        'imeiLocked',
        'planTypes',
      ];

      if (
        // eslint-disable-next-line eqeqeq
        (showContracted == 'false' ||
          defaultShowContracted === 'false' ||
          !showContracted) &&
        hasCustomersRoles &&
        hasPermission(rolesConstants.INVENT_CUSTOMER_NAME, 'inventory') &&
        showSelectCustomer
      ) {
        defaultValuesString.push('customerId');
      }

      const defaultValuesOnParams = filtersData
        .map(itemOnFilter => {
          let result;
          if (itemOnFilter.date) {
            const startValue = searchParams[itemOnFilter.date.start];
            const endValue = searchParams[itemOnFilter.date.end];
            result =
              (startValue || endValue) &&
              `${startValue || ''}/${endValue || ''}`;
          } else if (itemOnFilter.range) {
            const fromValue = searchParams[itemOnFilter.range.from];
            const toValue = searchParams[itemOnFilter.range.to];
            result =
              (fromValue || toValue) && `${fromValue || ''} ${toValue || ''}`;
          } else if (SEARCH_UPLOADER_ITEMS.includes(itemOnFilter.value)) {
            const valueInSearchUploader =
              searchUploaderValues?.[itemOnFilter.value as SearchUploaderItems];
            if (valueInSearchUploader) {
              result = `${valueInSearchUploader[0]}, ...`;
            } else {
              result = searchParams[itemOnFilter.value];
            }
          } else {
            result = searchParams[itemOnFilter.value];
          }

          return {
            defaultValue: result,
            ...itemOnFilter,
          };
        })
        .filter(item => item.defaultValue !== undefined);

      const findDefaultFilters = filtersData.filter(
        itemOnFilter =>
          defaultValuesString.includes(itemOnFilter.value) &&
          itemOnFilter.value &&
          !defaultValuesOnParams.some(
            item => item.value === itemOnFilter.value,
          ),
      );

      return defaultValuesOnParams.length > 0
        ? [...findDefaultFilters, ...defaultValuesOnParams]
        : findDefaultFilters;
    },
    [
      getTablePreferences,
      tableKey,
      showContracted,
      hasCustomersRoles,
      hasPermission,
      showSelectCustomer,
      filtersData,
      searchParams,
      searchUploaderValues,
    ],
  );

  const [itemsToMap, setItemsToMap] = useState<FilterQueryItems[] | undefined>([
    ...defaultValues(),
  ]);

  const handleTabsChange = useCallback(
    (key: string) => {
      if (!isEndCustomer) {
        setSearchParams({
          showContracted: key,
          stock: key !== 'true' ? undefined : stock,
        });
        if (key === 'true') {
          setItemsToMap([
            ...defaultValues(key).filter(item => item.value !== 'customerId'),
          ]);
          return;
        }
        setItemsToMap([...defaultValues(key)]);
      }
    },
    [isEndCustomer, setSearchParams, stock, defaultValues],
  );

  const handleChange = (value: string, add: boolean, defaultValue?: string) => {
    const findItem = filtersData.find(filterItem => filterItem.value === value);
    const index = itemsToMap?.findIndex(
      filterItem => filterItem.value === value,
    );

    if (add && findItem) {
      setItemsToMap(previous =>
        previous
          ? previous.map((item, i) =>
              i === index ? { ...findItem, defaultValue } : item,
            )
          : [{ ...findItem, defaultValue }],
      );
    } else {
      const itemWithtouDefaultValues = itemsToMap?.map(item =>
        item.value === value ? { ...item, defaultValue: undefined } : item,
      );
      setItemsToMap(itemWithtouDefaultValues);
    }
  };

  const handleRemoveDefaultValues = () =>
    setItemsToMap(
      itemsToMap?.map(item => {
        return {
          ...item,
          defaultValue: undefined,
        };
      }),
    );

  const hasValuesInSearchUploader = useMemo(
    () =>
      searchUploaderValues &&
      Object.values(searchUploaderValues).some(val => !!val),
    [searchUploaderValues],
  );

  return (
    <S.InventoryContainer>
      {!tableOnly && (
        <>
          <RoleChecker role={rolesConstants.LINES_INFO}>
            <LinesInfo
              setSearchParams={setSearchParams}
              filters={{ showContracted: filters.showContracted }}
              handleChange={handleChange}
            />
          </RoleChecker>
          {!isRoot && !isEndCustomer && isMot && !isMotPay && (
            <Tabs
              type="card"
              defaultActiveKey="receive"
              activeKey={showContracted}
              onChange={handleTabsChange}
              className="inventory-tabs"
            >
              <TabPane
                key="false"
                tab={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gridGap: '8px',
                    }}
                  >
                    <Typography>
                      {t(
                        `visions-steps.${
                          isMotBusiness ? 'allocated' : 'receive'
                        }`,
                      )}
                    </Typography>
                    {isMotBusiness && (
                      <Tooltip title={t('visions-steps.info.allocated')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    )}
                    {eventsAlertsData?.items?.find(
                      (events: any) => events?.eventType === 'CONSUMPTION',
                    )?.hasAlertsToReceive &&
                      lastAlertDateFrom &&
                      showContracted === 'true' && (
                        <Tooltip title={t('has-alerts')}>
                          <ExclamationCircleOutlined
                            style={{
                              color: 'var(--primary-color)',
                              marginLeft: 8,
                            }}
                          />
                        </Tooltip>
                      )}
                  </div>
                }
              />
              <TabPane
                key="true"
                tab={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gridGap: '8px',
                    }}
                  >
                    <Typography>{t('visions-steps.pay')}</Typography>
                    {isMotBusiness && (
                      <Tooltip title={t('visions-steps.info.pay')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    )}
                    {eventsAlertsData?.items?.find(
                      (events: any) => events?.eventType === 'CONSUMPTION',
                    )?.hasAlertsToPay &&
                      lastAlertDateFrom &&
                      showContracted === 'false' && (
                        <Tooltip title={t('has-alerts')}>
                          <ExclamationCircleOutlined
                            style={{
                              color: 'var(--primary-color)',
                              marginLeft: 8,
                            }}
                          />
                        </Tooltip>
                      )}
                  </div>
                }
              />
            </Tabs>
          )}
        </>
      )}

      <Table
        // @ts-ignore
        columns={columns}
        data={inventoryData?.content}
        loading={!inventoryData?.content}
        title={t('devices')}
        tabs={
          !isRoot && isMot && showContracted === 'true'
            ? {
                activeKey: stock,
                hasAll: true,
                list: tabsOptions,
                onChange: handleTabFilterChange,
              }
            : undefined
        }
        pagination={{
          page,
          linesPerPage,
          total: inventoryData?.totalElements || 0,
        }}
        tableKey={tableKey}
        onTableChange={setSearchParams}
        selectable
        setSelectedRowKeys={(
          keys: any,
          rows: React.SetStateAction<IDevice[] | undefined>,
        ) => {
          setSelectedRows(rows);
        }}
        onRow={(deviceOnRow: IDevice) => ({
          onDoubleClick: () => {
            togglePreview(deviceOnRow);
          },
        })}
        queryFilters={{
          filters: filtersData,
          clearSearchParams: () => {
            handleRemoveDefaultValues();
            clearSearchParams();
            setSearchUploaderValues(undefined);
          },
          itemsToMap,
          setItemsToMap,
          hasValuesInFilters:
            hasValuesInExtraParams || !!hasValuesInSearchUploader,
        }}
        extraActions={extraActions}
        mainExtraAction={mainExtraActions}
        queryBuilder={
          <QueryBuilder
            clearSearchParams={clearSearchParams}
            setSearchParams={setSearchParams}
            itemsToMap={itemsToMap}
            handleChange={handleChange}
            defaultValues={defaultValues()}
            departmentId={customerId}
            onChangeSearchUploader={valueChanged => {
              setSearchUploaderValues(crrValues => ({
                ...crrValues,
                ...valueChanged,
              }));
            }}
          />
        }
      />
      {isOpenPreview && previewDevice && (
        <InventoryPreview
          isOpen={isOpenPreview}
          setIsOpen={setIsOpenPreview}
          deviceProps={previewDevice}
          showContracted={filters.showContracted}
          handleMutateInventoryData={handleMutateInventoryData}
          isRoot={isRoot}
          stock={stock}
        />
      )}

      <ActionsModalByTypes.Simcard
        visible={modalDeviceActionsVisibleMot}
        onClose={() => setModalDeviceActionsVisibleMot(false)}
        actionRole={actionRole}
        selectedDevices={selectedRows}
        isUpload={isUpload}
        showContracted={showContracted || isEndCustomer}
      />

      <DeviceActionModal
        visible={modalDeviceActionsVisible}
        actionKey={actionToExecute}
        devicesProps={selectedRows}
        onClose={() => setModalDeviceActionsVisible(false)}
      />
    </S.InventoryContainer>
  );
};

export default Inventory;
